<template>
  <div>
    <Banner />

    <div class="container-fluid">
      <div class="container" v-if="isPublic || isReviewer">
        <ReviewModeInfo class="mb-4" />

        <CreatePlanSummary :studentType="studentType" :studyOption="studyOption" :teachingPeriod="studyPreference.teachingPeriod"
          :startYear="studyPreference.startYear" />

        <!-- Alert state for Level 100 error -->
        <div class="page-alert warning mb-4" v-if="generalElectiveLevel100Alert">
          <div class="page-alert__icon"><i class="far fa-exclamation-circle"></i></div>
          <div>
            <div class="page-alert__title">You have selected too many level 100 units </div>
            <div class="page-alert__body">
              The total credit points for level 100 units across all component of the Course cannot exceed 30. Please amend your selection.
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col" v-if="!reviewMode">
            <h2 class="h3 mb-2">{{ __('label:select-units title') }}</h2>
            <p>Here’s what you need to do to get your course plan ready</p>
          </div>
          <div class="col" v-else>
            <h2 class="h3 mb-2">{{ __('label:review-units title') }}</h2>
            <div v-if="hasErrors" class="row">
              <div class="col-12">
                <div class="page-alert info mb-4">
                  <i class="page-alert__icon far fa-exclamation-circle"></i>
                  <div class="page-alert__body">
                    <p>Please review all errors below.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col text-right">
            <button class="btn btn-secondary" @click="baseStep.previous">Back</button>
            <button class="btn btn-primary" @click="next" :disabled="loading">Generate plan</button>
          </div>
        </div>

        <div class="row" v-if="reviewMode && missingUnits.length > 0">
          <div class="col-12">
            <div class="page-alert warning mb-4">
              <i class="page-alert__icon far fa-exclamation-circle"></i>
              <div class="page-alert__body">
                <p>The following selection{{missingUnits.length === 1 ? ' is' : 's are'}} no longer available.</p>
                <ul class="mb-0">
                  <li v-for="error in missingUnits" :key="error.unitId">
                    Selection <strong>{{error.unitId}}</strong> is no longer available in: <a
                      :href="`#anchor-${error.to}`"><strong>{{error.location}}</strong></a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="row" v-if="loading">
          <div class="page-content-state page-content-state-loading col-12">
            <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
            <div class="page-content-state__content">Loading course</div>
          </div>
        </div>

        <div v-if="course && !loading">
          <CourseStructure :structure="course.structure[0]" :location="location" :mandatory="true" />
        </div>

        <div class="row mt-3">
          <div class="col text-right">
            <button class="btn btn-secondary" @click="baseStep.previous">Back</button>
            <button class="btn btn-primary" @click="next" :disabled="loading">Generate plan</button>
          </div>
        </div>

      </div>
      <div v-else class="container">
        <Reauthenticate />
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss"></style>

<script>
import useBaseStep from 'client/js/composition/base-step'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import creditChangeCalculator from 'client/js/utils/credit-change-calculator.js'
import Banner from './common/Banner.vue'
import ReviewModeInfo from './common/ReviewModeInfo.vue'
import Reauthenticate from './common/Reauthenticate.vue'
import CreatePlanSummary from './CreatePlanSummary.vue'
import CourseStructure from './select-units/CourseStructure.vue'
import Curriculum from 'client/js/models/curriculum.js'
import StudentPreferences from 'common/models/student-preferences.js'
import { onlyUnique } from 'common/helpers/only-unique'

export default {
  // TODO: remove comment once the select-unit page is stable
  setup(props, context) {
    return {
      baseStep: useBaseStep(props, context),
      loading: false
    }
  },
  data() {
    return {
      location: 'root'
    }
  },
  computed: {
    ...mapGetters({
      isReviewer: 'global/isReviewer',
      course: 'courses/getSelectedCourse',
      isPublic: 'courses/isPublic',
      studyPreference: 'preferences/getSelectedStudyPreferences',
      selectedItems: 'units/getSelectedCurriculumItems',
      totalCredits: 'units/getTotalCredits',
      savedPlan: 'savedCourse/getSavedCoursePlan',
      generalElectiveLevel100Alert: 'units/getGeneralElectiveLevel100Alert',
      validationStates: 'savedCourse/getValidationStates',
      savedMissingUnits: 'savedCourse/getMissingUnits'
    }),
    studentType() {
      return StudentPreferences.courseloopOptionToStudentType(this.studyPreference.studentType).shortLabel
    },
    studyOption() {
      return StudentPreferences.courseloopOptionToStudyOption(this.studyPreference.studyOption).shortLabel
    },
    startPeriod() {
      return StudentPreferences.courseloopOptionToTeachingPeriod(this.studyPreference.teachingPeriod).shortLabel
    },
    missingUnitsFromSavedPlan: function () {
      return this.savedMissingUnits.filter(onlyUnique) || []
    },
    reviewMode: function () {
      return this.savedPlan != null
    },
    hasErrors: function () {
      if (!this.validationStates.unitsValid || !this.validationStates.planValid || this.savedPlan.coursePlan?.siloedUnits?.length > 0) return true
    },
    // return unit locations, for regex matching
    unitLocations: function () {
      let locations = []
      this.selectedItems.forEach((unit) => {
        locations.push(unit.location)
      })
      return locations
    },
    missingUnits: function () {
      let missingUnitsArray = []
      this.missingUnitsFromSavedPlan?.forEach((unit) => missingUnitsArray.push(this.prettifyError(unit)))
      return missingUnitsArray
    }
  },
  methods: {
    ...mapActions({
      clearHash: 'plans/clearHash'
    }),
    // __:i18n,
    logSelectedItems() {
      console.log(this.selectedItems)
    },
    prettifyError(error) {
      let splitArray = []
      let prettyError = {
        unitId: '',
        location: '',
        to: ''
      }

      let split = error.split('>')
      prettyError.to = split[1].split('-')[0].replace(' ', '').toLowerCase()
      prettyError.unitId = split[split.length - 1].split('v')[0]

      split.forEach((item) => {
        if (
          !(
            item.match(/^root$/) ||
            item.match(/^(?:MN-\w{2,4}|MJ-\w{2,4}|\w{3,4}\d{3,10})v\d{1,2}$/) ||
            item.match(/^(?:MN-\w{2,4}|MJ-\w{2,4}|\w{3,4}\d{3,10})$/)
          )
        ) {
          splitArray.push(item.split('-')[0])
        }
      })

      prettyError.location = splitArray.join(', ')

      return prettyError
    },
    next() {
      this.clearHash()
      this.baseStep.next()
    }
  },
  mounted: async function () {
    // if (!this.isPublic && !this.isReviewer) {
    //   await this.$store.dispatch('global/reauthenticateUser')
    // }
    this.baseStep.valid(true)
  },
  beforeDestroy() {},
  components: { CourseStructure, Banner, CreatePlanSummary, ReviewModeInfo, Reauthenticate }
}
</script>

<style></style>
